import { rem } from 'polished'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import LegacyThingsToDoAddressField from './LegacyThingsToDoAddresses/LegacyThingsToDoAddressField'

import Group from 'components/utils/Group'

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

interface Props {
  tripId: string
}

function CustomThingToDoFields({ tripId }: Props) {
  const { urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext()
  const { watch, control, register } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const place = watch('place')

  const nameLabel = intl.formatMessage({
    id: 'eVaN6pxyvW',
    defaultMessage: 'Name',
    description: 'Form label for name field in custom thing to do trip item editor',
  })

  const namePlaceholder = intl.formatMessage({
    id: 'q3kwCxrQui',
    defaultMessage: 'Enter a name',
    description: 'Placeholder text for name input in custom thing to do trip item editor',
  })

  const dateLabel = intl.formatMessage({
    id: 'Wsmo8ALkMr',
    defaultMessage: 'Date',
    description: 'Form label for date field in custom thing to do trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'wg3web3Vfd',
    defaultMessage: 'Time',
    description: 'Form label for time field in custom thing to do trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: '+aZ5CAbT/k',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in custom thing to do trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'C+4s2b+eNl',
    defaultMessage: 'Price',
    description: 'Form label for price field in custom thing to do trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'YiQynCnZuv',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in custom thing to do trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: 'dGZJt39+S5',
    defaultMessage: 'Note',
    description: 'Form label for notes field in custom thing to do trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: '11royVeeZs',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field in the pricing or cost breakdown section',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQ',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const notePlaceholder = intl.formatMessage({
    id: '8lL+HnK60a',
    defaultMessage: 'Add note here',
    description: 'Input placeholder text for notes textarea field in the trip planner item editor modal',
  })

  return (
    <Group direction="vertical" gap={16}>
      <Input {...register('name')} label={nameLabel} placeholder={namePlaceholder} />

      <LegacyThingsToDoAddressField key={place} tripId={tripId} />

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={dateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            name={field.name}
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />
      <ShowMore>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notePlaceholder}
          label={noteLabel}
          size="tall"
          resize="vertical"
        />
      </ShowMore>
    </Group>
  )
}

export default CustomThingToDoFields
